import { useMemo, useState } from "react";

import {
  IAddPatientRequest,
  IHospitalDetail,
  IWardCustomField,
} from "../../../../@types/admin/AdminHospitalDetails";
import { IWard } from "../../../../@types/admin/AdminWard";
import { StandardFieldCode } from "../../../../@types/StandardField";
import { useAddHospitalPatient } from "../../../../hooks/mutations/admin/hospitalDetails";
import {
  useWardCustomFields,
  useWardStandardFields,
} from "../../../../hooks/queries/admin/hospitalDetails";
import { useConditionTypeOptions } from "../../../../hooks/queries/admin/selectValues";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  AdminDateField,
  AdminSelectField,
  CheckboxField,
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  WarningNotice,
} from "../../shared";

interface IAddPatientModalProps {
  hospital: IHospitalDetail;
  wards: IWard[];
  onDone: () => void;
}

/** Retool's options; the column stores the single lowercase character. */
const SEX_OPTIONS = [
  { value: "", label: "Not recorded" },
  { value: "f", label: "Female" },
  { value: "m", label: "Male" },
];

const ETHNICITY_OPTIONS = [
  { value: "", label: "Not recorded" },
  { value: "white", label: "White" },
  { value: "black", label: "Black" },
  { value: "asian", label: "Asian" },
  { value: "other", label: "Other" },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The `<input type>` a custom field's value type needs. */
function inputTypeFor(type: IWardCustomField["type"]): string {
  if (type === "Integer" || type === "Decimal") return "number";
  if (type === "Date") return "date";
  return "text";
}

/**
 * One custom field's input, chosen by its declared value type. Multiselect
 * falls through to free text deliberately: the add-patient form takes a single
 * value per field, and a comma-separated string is what the API stores for a
 * multiselect answer entered here.
 */
function CustomFieldInput({
  field,
  value,
  onChange,
}: {
  field: IWardCustomField;
  value: string;
  onChange: (value: string) => void;
}) {
  if (field.type === "Dropdown") {
    return (
      <AdminSelectField
        id={`cf-${field.customFieldDefinitionId}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Not set</option>
        {field.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </AdminSelectField>
    );
  }

  if (field.type === "YesNo") {
    return (
      <CheckboxField>
        <input
          type="checkbox"
          checked={value === "true"}
          onChange={(e) => onChange(e.target.checked ? "true" : "false")}
        />
        Yes
      </CheckboxField>
    );
  }

  return (
    <FilterInput
      id={`cf-${field.customFieldDefinitionId}`}
      type={inputTypeFor(field.type)}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/**
 * Adds a patient to one of the hospital's wards — Retool's
 * `drawerFrame_addPatient` (EN-5172).
 *
 * ⚠️ Creates a patient record, and an account the patient can be enrolled
 * into. Retool submitted straight from the form; this confirms, because the
 * write is not undoable from this page — an unwanted patient has to be archived
 * afterwards.
 *
 * **The ward's custom fields are included, which Retool's form skipped.** A
 * ward can mark a custom field `showInAddPatient` and `isRequired`, and
 * `AddPatientRequestDto` accepts `customFields` — but Retool's body listed ten
 * fixed keys and never sent them, so a required field was silently absent on
 * every patient added here. The fields appear once a ward is chosen, since the
 * configuration is per ward.
 */
export function AddPatientModal({
  hospital,
  wards,
  onDone,
}: IAddPatientModalProps) {
  const { showToast } = useToast();
  const { addPatient, isAddingPatient } = useAddHospitalPatient();
  const { conditionTypes } = useConditionTypeOptions();

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [wardId, setWardId] = useState("");
  const [conditionTypeId, setConditionTypeId] = useState("");
  const [hospitalPatientId, setHospitalPatientId] = useState("");
  const [dob, setDob] = useState("");
  const [sex, setSex] = useState("");
  const [ethnicity, setEthnicity] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [customValues, setCustomValues] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState(false);

  const { wardCustomFields } = useWardCustomFields(
    wardId ? Number(wardId) : undefined
  );

  // EN-5192: the ward decides which built-in fields are shown and which are mandatory. Until a
  // ward is chosen the query is idle and the defaults apply — every field shown, none mandatory.
  const { wardStandardFields } = useWardStandardFields(
    wardId ? Number(wardId) : undefined
  );

  const standardShown = (code: StandardFieldCode) => {
    const field = wardStandardFields.find((f) => f.code === code);
    return field ? field.enabled : true;
  };

  const standardRequired = (code: StandardFieldCode) => {
    const field = wardStandardFields.find((f) => f.code === code);
    return field ? field.enabled && field.required : false;
  };

  /** Only the fields this ward asks for on the add-patient form. */
  const addPatientFields = useMemo(
    () =>
      wardCustomFields
        .filter((field) => field.isEnabled && field.showInAddPatient)
        .sort((a, b) => a.displayOrder - b.displayOrder),
    [wardCustomFields]
  );

  const missingRequiredCustomField = addPatientFields.some(
    (field) => field.isRequired && !customValues[field.customFieldDefinitionId]
  );

  const emailError = (() => {
    if (!email.trim()) return "Email is required.";
    if (!EMAIL_PATTERN.test(email.trim())) {
      return "That doesn't look like an email address.";
    }
    return undefined;
  })();

  const errors = {
    email: emailError,
    firstName: firstName.trim() ? undefined : "First name is required.",
    lastName: lastName.trim() ? undefined : "Last name is required.",
    wardId: wardId ? undefined : "Ward is required.",
    conditionTypeId: conditionTypeId
      ? undefined
      : "Condition type is required.",
    hospitalPatientId:
      standardRequired("HospitalPatientId") && !hospitalPatientId.trim()
        ? "Hospital patient ID is required."
        : undefined,
    dob:
      standardRequired("DateOfBirth") && !dob
        ? "Date of birth is required."
        : undefined,
    sex: standardRequired("Sex") && !sex ? "Sex is required." : undefined,
    ethnicity:
      standardRequired("Ethnicity") && !ethnicity
        ? "Ethnicity is required."
        : undefined,
    phoneNumber:
      standardRequired("PhoneNumber") && !phoneNumber.trim()
        ? "Phone number is required."
        : undefined,
  };
  const hasErrors =
    Object.values(errors).some(Boolean) || missingRequiredCustomField;

  const selectedWard = wards.find((ward) => String(ward.id) === wardId);

  const handleSubmit = async () => {
    setTouched(true);
    if (hasErrors) return;

    const request: IAddPatientRequest = {
      email: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      conditionTypeId: Number(conditionTypeId),
      wardId: Number(wardId),
      // EN-5192: a field the ward hides is never sent, even if it was filled in before the ward
      // changed — otherwise the server stores a value the operator can no longer see or correct.
      hospitalPatientId: standardShown("HospitalPatientId")
        ? hospitalPatientId.trim()
        : "",
      dob: standardShown("DateOfBirth") ? dob || null : null,
      sex: standardShown("Sex") ? sex || null : null,
      ethnicity: standardShown("Ethnicity") ? ethnicity || null : null,
      phoneNumber: standardShown("PhoneNumber")
        ? phoneNumber.trim() || null
        : null,
      customFields: addPatientFields
        .map((field) => ({
          customFieldDefinitionId: field.customFieldDefinitionId,
          value: customValues[field.customFieldDefinitionId] ?? null,
        }))
        .filter((entry) => entry.value !== null && entry.value !== ""),
    };

    try {
      await addPatient({ request });
      showToast("Patient successfully added.", "success");
      onDone();
    } catch (error) {
      // The endpoint rejects a duplicate email with a message naming it;
      // Retool put that in its own `addPatientError` state below the form.
      showToast(
        errorToastMessage(error, "There was an error adding this patient."),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Add patient</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Creating a patient in <strong>{hospital.name}</strong>.
        </ModalBodyText>

        <FilterGroup>
          <FilterLabel htmlFor="add-patient-ward">Ward</FilterLabel>
          <AdminSelectField
            id="add-patient-ward"
            value={wardId}
            onChange={(e) => setWardId(e.target.value)}
          >
            <option value="">Select a ward</option>
            {wards.map((ward) => (
              <option key={ward.id} value={String(ward.id)}>
                {ward.name}
              </option>
            ))}
          </AdminSelectField>
          {touched && errors.wardId && <FieldError>{errors.wardId}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="add-patient-condition">
            Condition type
          </FilterLabel>
          <AdminSelectField
            id="add-patient-condition"
            value={conditionTypeId}
            onChange={(e) => setConditionTypeId(e.target.value)}
          >
            <option value="">Select a condition type</option>
            {conditionTypes.map((type) => (
              <option key={type.id} value={String(type.id)}>
                {type.name}
              </option>
            ))}
          </AdminSelectField>
          {touched && errors.conditionTypeId && (
            <FieldError>{errors.conditionTypeId}</FieldError>
          )}
          <FieldHint>
            Decides which measurement cards the patient&apos;s app asks for.
          </FieldHint>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="add-patient-email">Email</FilterLabel>
          <FilterInput
            id="add-patient-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {touched && errors.email && <FieldError>{errors.email}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="add-patient-first-name">First name</FilterLabel>
          <FilterInput
            id="add-patient-first-name"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          {touched && errors.firstName && (
            <FieldError>{errors.firstName}</FieldError>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="add-patient-last-name">Last name</FilterLabel>
          <FilterInput
            id="add-patient-last-name"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          {touched && errors.lastName && (
            <FieldError>{errors.lastName}</FieldError>
          )}
        </FilterGroup>

        {standardShown("HospitalPatientId") && (
          <FilterGroup>
            <FilterLabel htmlFor="add-patient-mrn">
              Hospital patient ID
              {standardRequired("HospitalPatientId") ? " *" : ""}
            </FilterLabel>
            <FilterInput
              id="add-patient-mrn"
              type="text"
              maxLength={70}
              value={hospitalPatientId}
              onChange={(e) => setHospitalPatientId(e.target.value)}
            />
            {touched && errors.hospitalPatientId && (
              <FieldError>{errors.hospitalPatientId}</FieldError>
            )}
          </FilterGroup>
        )}

        {standardShown("DateOfBirth") && (
          <FilterGroup>
            <FilterLabel htmlFor="add-patient-dob">
              Date of birth{standardRequired("DateOfBirth") ? " *" : ""}
            </FilterLabel>
            <AdminDateField
              id="add-patient-dob"
              value={dob}
              onChange={setDob}
            />
            {touched && errors.dob && <FieldError>{errors.dob}</FieldError>}
          </FilterGroup>
        )}

        {standardShown("Sex") && (
          <FilterGroup>
            <FilterLabel htmlFor="add-patient-sex">
              Sex{standardRequired("Sex") ? " *" : ""}
            </FilterLabel>
            <AdminSelectField
              id="add-patient-sex"
              value={sex}
              onChange={(e) => setSex(e.target.value)}
            >
              {SEX_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </AdminSelectField>
            {touched && errors.sex && <FieldError>{errors.sex}</FieldError>}
          </FilterGroup>
        )}

        {standardShown("Ethnicity") && (
          <FilterGroup>
            <FilterLabel htmlFor="add-patient-ethnicity">
              Ethnicity{standardRequired("Ethnicity") ? " *" : ""}
            </FilterLabel>
            <AdminSelectField
              id="add-patient-ethnicity"
              value={ethnicity}
              onChange={(e) => setEthnicity(e.target.value)}
            >
              {ETHNICITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </AdminSelectField>
            {touched && errors.ethnicity && (
              <FieldError>{errors.ethnicity}</FieldError>
            )}
          </FilterGroup>
        )}

        {standardShown("PhoneNumber") && (
          <FilterGroup>
            <FilterLabel htmlFor="add-patient-phone">
              Phone number{standardRequired("PhoneNumber") ? " *" : ""}
            </FilterLabel>
            <FilterInput
              id="add-patient-phone"
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
            <FieldHint>
              E.164 (+353871234567) — it feeds the alert SMS directly.
            </FieldHint>
            {touched && errors.phoneNumber && (
              <FieldError>{errors.phoneNumber}</FieldError>
            )}
          </FilterGroup>
        )}

        {addPatientFields.length > 0 && (
          <>
            <FilterGroup>
              <FilterLabel>
                {selectedWard?.name ?? "Ward"} custom fields
              </FilterLabel>
              <FieldHint>
                Configured for this ward. Retool&apos;s form never sent these,
                so a required one was silently missing.
              </FieldHint>
            </FilterGroup>

            {addPatientFields.map((field) => (
              <FilterGroup key={field.customFieldDefinitionId}>
                <FilterLabel htmlFor={`cf-${field.customFieldDefinitionId}`}>
                  {field.label}
                  {field.isRequired ? " *" : ""}
                </FilterLabel>
                <CustomFieldInput
                  field={field}
                  value={customValues[field.customFieldDefinitionId] ?? ""}
                  onChange={(value) =>
                    setCustomValues((previous) => ({
                      ...previous,
                      [field.customFieldDefinitionId]: value,
                    }))
                  }
                />
                {touched &&
                  field.isRequired &&
                  !customValues[field.customFieldDefinitionId] && (
                    <FieldError>{field.label} is required.</FieldError>
                  )}
              </FilterGroup>
            ))}
          </>
        )}

        <WarningNotice>
          This creates a patient record in{" "}
          <strong>{selectedWard?.name ?? "the selected ward"}</strong>. It
          cannot be undone from here — an unwanted patient has to be archived.
        </WarningNotice>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isAddingPatient}
            onClick={onDone}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isAddingPatient}
            onClick={handleSubmit}
          >
            {isAddingPatient ? "Adding..." : "Add patient"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
